export const CMS_PAGE_TYPES = {
  ABOUT_US: "ABOUT_US",
  CONTACT_US: "CONTACT_US",
  PRIVACY_POLICY: "PRIVACY_POLICY",
  REFUND_POLICY: "REFUND_POLICY",
  DOMAIN_REDIRECTION_POLICY: "DOMAIN_REDIRECTION_POLICY",
};

export const CMS_PAGE_TYPE_LABELS = {
  ABOUT_US: "About Us",
  CONTACT_US: "Contact Us",
  PRIVACY_POLICY: "Privacy Policy",
  REFUND_POLICY: "Refund & Cancellation Policy",
  DOMAIN_REDIRECTION_POLICY: "Domain Redirection Policy",
};

export const CMS_PAGE_ROUTES = {
  ABOUT_US: "/about-us",
  CONTACT_US: "/contact",
  PRIVACY_POLICY: "/privacy-policy",
  REFUND_POLICY: "/refund-cancellation-policy",
  DOMAIN_REDIRECTION_POLICY: "/domain-redirection-policy",
};

export const TERMS_MODULES = {
  PLATFORM: "PLATFORM",
  AUCTION: "AUCTION",
  INFRASTRUCTURE_LEASING: "INFRASTRUCTURE_LEASING",
  CONTRACT_MANUFACTURING: "CONTRACT_MANUFACTURING",
  PROCURE_TO_PAY: "PROCURE_TO_PAY",
  QUALITY_ASSESSMENT: "QUALITY_ASSESSMENT",
  CONSULTANTS: "CONSULTANTS",
  LEGAL: "LEGAL",
  LOGISTICS: "LOGISTICS",
};

export const TERMS_MODULE_LABELS = {
  PLATFORM: "Overall Platform",
  AUCTION: "Auction",
  INFRASTRUCTURE_LEASING: "Infrastructure Leasing",
  CONTRACT_MANUFACTURING: "Contract Manufacturing",
  PROCURE_TO_PAY: "Procure to Pay",
  QUALITY_ASSESSMENT: "Quality Assessment",
  CONSULTANTS: "Consultants",
  LEGAL: "Legal",
  LOGISTICS: "Logistics",
};

/** Map URL path prefixes to terms module keys for acceptance gating */
export const ROUTE_TERMS_MODULE_MAP = [
  { prefix: "/dashboard/p2p", module: TERMS_MODULES.PROCURE_TO_PAY },
  { prefix: "/dashboard/admin/p2p", module: TERMS_MODULES.PROCURE_TO_PAY },
  { prefix: "/auction", module: TERMS_MODULES.AUCTION },
  { prefix: "/dashboard/auction", module: TERMS_MODULES.AUCTION },
  { prefix: "/infrastructure-leasing", module: TERMS_MODULES.INFRASTRUCTURE_LEASING },
  { prefix: "/dashboard/infrastructure", module: TERMS_MODULES.INFRASTRUCTURE_LEASING },
  { prefix: "/contract-manufacturers", module: TERMS_MODULES.CONTRACT_MANUFACTURING },
  { prefix: "/dashboard/cm", module: TERMS_MODULES.CONTRACT_MANUFACTURING },
  { prefix: "/dashboard/contract-manufacturing", module: TERMS_MODULES.CONTRACT_MANUFACTURING },
  { prefix: "/logistics-providers", module: TERMS_MODULES.LOGISTICS },
  { prefix: "/logistics", module: TERMS_MODULES.LOGISTICS },
  { prefix: "/dashboard/logistics", module: TERMS_MODULES.LOGISTICS },
  { prefix: "/dashboard/qa", module: TERMS_MODULES.QUALITY_ASSESSMENT },
  { prefix: "/qa", module: TERMS_MODULES.QUALITY_ASSESSMENT },
  { prefix: "/dashboard/consultants", module: TERMS_MODULES.CONSULTANTS },
  { prefix: "/dashboard/legal", module: TERMS_MODULES.LEGAL },
  { prefix: "/procurement-services", module: TERMS_MODULES.PROCURE_TO_PAY },
  { prefix: "/marketplace", module: TERMS_MODULES.PLATFORM },
  { prefix: "/dashboard", module: TERMS_MODULES.PLATFORM },
];

export const resolveTermsModuleFromPath = (pathname = "") => {
  const match = ROUTE_TERMS_MODULE_MAP.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match?.module ?? null;
};

export const CMS_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

export const CMS_PAGE_TYPE_OPTIONS = Object.entries(CMS_PAGE_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/** CMS dashboard filter — includes Terms alongside static CMS page types */
export const CMS_DASHBOARD_PAGE_TYPE_OPTIONS = [
  { value: "TERMS", label: "Terms & Conditions" },
  ...CMS_PAGE_TYPE_OPTIONS,
];

/** Public CMS page navigation (excludes Contact Us + About Us — dedicated app pages) */
export const PUBLIC_CMS_PAGE_OPTIONS = Object.entries(CMS_PAGE_ROUTES)
  .filter(([value]) => value !== "CONTACT_US" && value !== "ABOUT_US")
  .map(([value, path]) => ({
    value,
    label: CMS_PAGE_TYPE_LABELS[value],
    path,
  }));

/** Default hero subtitle per public CMS page type */
export const CMS_PUBLIC_PAGE_HERO_SUBTITLE = {
  ABOUT_US: "Learn about Trocanet, our mission, and the platform we provide.",
  CONTACT_US: "Find official contact details and support information.",
  PRIVACY_POLICY: "Read how Trocanet collects, uses, and protects your data.",
  REFUND_POLICY: "Understand our refund and cancellation policies.",
  DOMAIN_REDIRECTION_POLICY: "Review our domain redirection and usage policy.",
};

/** Fallback intro on the content card when no description is set */
export const CMS_PUBLIC_PAGE_INTRO = {
  ABOUT_US:
    "This page contains official information about Trocanet and the B2B platform services we provide.",
  CONTACT_US: "Official contact and support information for the Trocanet platform.",
  PRIVACY_POLICY: "This policy explains how personal and business data is handled on Trocanet.",
  REFUND_POLICY: "This policy outlines refund eligibility, cancellations, and related procedures.",
  DOMAIN_REDIRECTION_POLICY: "This policy governs domain redirection rules on the Trocanet platform.",
};

/** Quick-access card copy on the CMS dashboard */
export const CMS_DASHBOARD_QUICK_CARDS = [
  {
    kind: "terms",
    title: "Terms & Conditions",
    description: "Legal terms & policies",
    icon: "terms",
    to: "/super-admin/cms/terms",
  },
  {
    pageType: "ABOUT_US",
    slug: "about-us",
    description: "Company information",
    icon: "building",
  },
  {
    pageType: "CONTACT_US",
    slug: "contact-us",
    description: "Contact details",
    icon: "envelope",
  },
  {
    pageType: "PRIVACY_POLICY",
    slug: "privacy-policy",
    description: "Data & privacy",
    icon: "shield",
  },
  {
    pageType: "REFUND_POLICY",
    slug: "refund-cancellation-policy",
    description: "Refunds policy",
    icon: "undo",
  },
  {
    pageType: "DOMAIN_REDIRECTION_POLICY",
    slug: "domain-redirection-policy",
    description: "Domain policy",
    icon: "globe",
  },
];

export const TERMS_MODULE_OPTIONS = Object.entries(TERMS_MODULE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/** Sidebar labels on the public Terms & Conditions page. */
export const TERMS_MODULE_NAV_LABELS = {
  PLATFORM: "Overall Platform",
  AUCTION: "Auction",
  INFRASTRUCTURE_LEASING: "Infrastructure Leasing",
  CONTRACT_MANUFACTURING: "Contract Manufacturing",
  PROCURE_TO_PAY: "Procure-to-Pay Engine",
  QUALITY_ASSESSMENT: "Quality Assessment Professionals",
  CONSULTANTS: "Consultants",
  LEGAL: "Legal Professionals",
  LOGISTICS: "Logistics",
};

/** Full page titles used in the public Terms hero. */
export const TERMS_MODULE_PAGE_TITLES = {
  PLATFORM: "Terms & Conditions – Overall Platform",
  AUCTION: "Auction Module – Terms & Conditions",
  INFRASTRUCTURE_LEASING: "Infrastructure Leasing Module – Terms & Conditions",
  CONTRACT_MANUFACTURING: "Contract Manufacturing Module – Terms & Conditions",
  PROCURE_TO_PAY: "Procure-to-Pay Engine – Terms & Conditions",
  QUALITY_ASSESSMENT: "Quality Assessment Professionals – Terms & Conditions",
  CONSULTANTS: "Consultants – Terms & Conditions",
  LEGAL: "Legal Professionals – Terms & Conditions",
  LOGISTICS: "Logistics Module – Terms & Conditions",
};

/** Blue information notice under accordion sections. */
export const TERMS_MODULE_CALLOUTS = {
  PLATFORM:
    "These terms apply to the overall Trocanet platform. Each module also has supplementary terms that apply when you use that module.",
  AUCTION:
    "These Auction Module Terms supplement the Overall Platform Terms & Conditions. In case of any inconsistency, these module-specific terms shall govern auction activities conducted through the Trocanet Marketplace.",
  INFRASTRUCTURE_LEASING:
    "These Infrastructure Leasing Terms supplement the Overall Platform Terms & Conditions. In case of any inconsistency, these module-specific terms shall govern infrastructure leasing activities conducted through the Trocanet Marketplace.",
  CONTRACT_MANUFACTURING:
    "These Contract Manufacturing Terms supplement the Overall Platform Terms & Conditions. If any conflict exists, these module-specific terms shall prevail.",
  PROCURE_TO_PAY:
    "This Procure-to-Pay Engine Policy supplements the Overall Platform Terms & Conditions. In case of any inconsistency, the Procure-to-Pay specific terms shall prevail for procurement-related activities.",
  QUALITY_ASSESSMENT:
    "These Quality Assessment Terms supplement the Overall Platform Terms & Conditions. In the event of any inconsistency, these module-specific terms shall apply to quality assessment services.",
  CONSULTANTS:
    "These Consultant Terms supplement the Overall Platform Terms & Conditions. In the event of any inconsistency, these module-specific terms shall govern consulting engagements conducted through the Trocanet Marketplace.",
  LEGAL:
    "These Legal Professionals Terms supplement the Overall Platform Terms & Conditions. In case of any inconsistency, these module-specific terms shall govern legal service engagements conducted through the Trocanet Marketplace.",
  LOGISTICS:
    "These Logistics Module Terms supplement the Overall Platform Terms & Conditions. In the event of any inconsistency, these module-specific terms shall govern logistics services, shipment management, and transportation activities conducted through the Trocanet Marketplace.",
};

export const TERMS_MODULE_NAV_OPTIONS = Object.keys(TERMS_MODULE_LABELS).map((value) => ({
  value,
  label: TERMS_MODULE_NAV_LABELS[value] || TERMS_MODULE_LABELS[value],
}));

/** Short intro copy shown on the public terms page summary card */
export const TERMS_MODULE_INTRO = {
  PLATFORM:
    "These terms and conditions apply to the overall use of Trocanet B2B Marketplace and outline how buyers, sellers and partners should use the platform.",
  AUCTION:
    "These Auction Module Terms explain how buyers and sellers participate in Trocanet auctions, including bidding, payments, settlement, and prohibited practices.",
  INFRASTRUCTURE_LEASING:
    "These Infrastructure Leasing Module Terms govern how businesses list, request, match, and lease infrastructure assets on Trocanet.",
  CONTRACT_MANUFACTURING:
    "These terms govern buyers and contract manufacturers using Trocanet for OEM, ODM, private label, and custom manufacturing services.",
  PROCURE_TO_PAY:
    "These terms govern the use of Trocanet's digital Procure-to-Pay Engine for sourcing, purchase requisitions, purchase orders, invoice management, approvals, supplier collaboration, and payment workflows.",
  QUALITY_ASSESSMENT:
    "These terms govern the engagement of quality assessment professionals for inspections, audits, certifications, laboratory testing, compliance verification, and quality reporting through the Trocanet Marketplace.",
  CONSULTANTS:
    "These terms govern businesses and professional consultants using Trocanet for advisory, strategy, technical consulting, business transformation, financial consulting, IT consulting, and project-based professional services.",
  LEGAL:
    "These terms govern businesses and verified legal professionals using the Trocanet Marketplace for legal advisory, contract drafting, agreement review, compliance, intellectual property, dispute resolution, and corporate legal services.",
  LOGISTICS:
    "These terms govern businesses, logistics service providers, freight forwarders, transporters, and delivery partners using the Trocanet Logistics Module for shipment booking, transportation, freight management, warehousing, tracking, and delivery services.",
};

export const CMS_LEGAL_LINKS = [
  { label: "About Us", to: CMS_PAGE_ROUTES.ABOUT_US },
  { label: "Contact Us", to: CMS_PAGE_ROUTES.CONTACT_US },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Privacy Policy", to: CMS_PAGE_ROUTES.PRIVACY_POLICY },
  { label: "Refund & Cancellation Policy", to: CMS_PAGE_ROUTES.REFUND_POLICY },
  { label: "Domain Redirection Policy", to: CMS_PAGE_ROUTES.DOMAIN_REDIRECTION_POLICY },
];
